import { DOCS_TREE } from './docs-tree';

import type { DevToolKey } from '@/devTools/config';
import type { DevToolsMap } from '@/hooks/use-dev-tool-toggle';
import type { TLAIDefinedRoles, TLAIUserInfo } from '@/types/api';
import { userHasRoles } from '@/utils/roles';

/** A page heading (`h2`/`h3`) surfaced in the "On this page" table of contents. */
export type DocHeading = {
  id: string;
  text: string;
  level: 2 | 3;
};

// A node in the documentation tree
export type DocNode = {
  slug: string;
  title: string;
  roles?: TLAIDefinedRoles[];
  devTool?: DevToolKey;
  contentPath?: string;
  /** Set instead of `contentPath` on a page rendered from the node registry rather than from MDX. */
  nodeName?: string;
  collapsed?: boolean;
  children?: DocNode[];
  headings?: DocHeading[];
};

/**
 * Resolves a documentation splat path to the chain of nodes from root to target.
 *
 * @param splat - The requested path, e.g. `'engineering/workflows'` (empty for home).
 * @param tree - Tree to resolve against; pass the merged tree to reach pages that
 *   are not in the static one (see `mergeLoomNodeDocs`). Defaults to `DOCS_TREE`.
 * @returns The node chain (`[section, …, page]`), or `null` when no node matches.
 * @example
 * resolveDocPath('agent/conversation/skills') // [agent, conversation, skills]
 * resolveDocPath('nope') // null
 */
export function resolveDocPath(splat: string, tree: DocNode[] = DOCS_TREE): DocNode[] | null {
  const segments = splat.split('/').filter(Boolean);
  if (segments.length === 0) {
    const home = tree.find((node) => node.slug === '');
    return home ? [home] : null;
  }

  const chain: DocNode[] = [];
  let level: DocNode[] = tree;
  for (const segment of segments) {
    const node = level.find((candidate) => candidate.slug === segment);
    if (!node) return null;
    chain.push(node);
    level = node.children ?? [];
  }
  return chain;
}

/**
 * Whether a single node is visible: its role group (if any) is satisfied and
 * its feature flag (if any) is enabled.
 */
function canViewNode(user: TLAIUserInfo, node: DocNode, devTools: DevToolsMap): boolean {
  if (node.roles && !userHasRoles(user, node.roles)) return false;
  if (node.devTool && !devTools[node.devTool]) return false;
  return true;
}

/**
 * Whether the user may view the page at the end of a resolved node chain.
 *
 * Enforces inherited gating: every node in the chain must pass its role group
 * (AND across groups, `userHasRoles` ANY within each) and its feature flag.
 *
 * @param user - The current user.
 * @param chain - A node chain from {@link resolveDocPath}.
 * @param devTools - Evaluated feature flags for the current user/tenant.
 * @returns `true` when the user satisfies every gating group in the chain.
 */
export function canViewDoc(user: TLAIUserInfo, chain: DocNode[], devTools: DevToolsMap): boolean {
  return chain.every((node) => canViewNode(user, node, devTools));
}

/**
 * Returns a role-filtered copy of the tree for the sidebar: nodes the user
 * cannot view are dropped, and sections left with no page and no visible child
 * are pruned.
 *
 * @param user - The current user.
 * @param devTools - Evaluated feature flags for the current user/tenant.
 * @param nodes - Nodes to filter (defaults to the whole tree).
 * @returns The visible subtree.
 */
export function visibleTree(user: TLAIUserInfo, devTools: DevToolsMap, nodes: DocNode[] = DOCS_TREE): DocNode[] {
  const visible: DocNode[] = [];
  for (const node of nodes) {
    if (!canViewNode(user, node, devTools)) continue;
    const children = node.children ? visibleTree(user, devTools, node.children) : undefined;
    if (!node.contentPath && !node.nodeName && !children?.length) continue;
    visible.push({ ...node, children });
  }
  return visible;
}
